import { FC, useState } from 'react';

import { Permission } from '@/api/permissions';
import { Button, Modal, Tooltip } from '@/shared/design-system';
import { CatalogGroup, PermissionAssignment } from '@/shared/modules/role-board/types';
import {
  actionableGroupPermissions,
  buildClearAllPatch,
  buildEnableAllPatch,
  countClearAllChanges,
  PermissionSelectionMap,
} from '@/utils/permission-catalog';

import { BulkActionsRow } from './styled';

export const BULK_ADD_BLOCKED_TOOLTIP =
  "Approval bypasses let the holder change material data without Gamma review, so they're granted one at a time.";

/**
 * Clicking 31 toggles to configure "Fund details" is not a workflow — a group
 * header carries `Enable all` / `Clear all`, touching only the rows the caller
 * can actually change (`2026-07-31-permissions-role-board-fe-prompt.md`, "Bulk
 * actions per group"). Rendered inside the group's expanded panel rather than
 * its accordion trigger: the trigger is itself a `<button>`, and nesting
 * interactive controls inside a button is both invalid HTML and breaks click
 * handling.
 */
export type GroupBulkActionsProps = {
  group: CatalogGroup;
  selection: PermissionSelectionMap;
  canGrantProtected: boolean;
  addBlocked?: boolean;
  onApply: (_add: PermissionAssignment[], _remove: Permission[]) => void;
};

type PendingAction = {
  label: string;
  add: PermissionAssignment[];
  remove: Permission[];
  sensitiveCount: number;
};

export const GroupBulkActions: FC<GroupBulkActionsProps> = ({
  group,
  selection,
  canGrantProtected,
  addBlocked = false,
  onApply,
}) => {
  const [pending, setPending] = useState<PendingAction | null>(null);

  const actionable = actionableGroupPermissions(group, selection, canGrantProtected);
  const clearCount = countClearAllChanges(group, selection);
  if (actionable.length === 0 && clearCount === 0 && !addBlocked) return null;

  const runOrConfirm = (label: string, add: PermissionAssignment[], remove: Permission[]) => {
    const touched = new Set([...add.map((assignment) => assignment.permission), ...remove]);
    // Sensitive groups confirm first. Only some of the touched rows may actually be sensitive, so
    // the count below tracks the subset — the confirm sentence must name that subset, not every
    // touched row.
    const sensitiveCount = group.permissions.filter(
      (permission) => touched.has(permission.permission) && permission.sensitive,
    ).length;

    if (sensitiveCount > 0) {
      setPending({ label, add, remove, sensitiveCount });

      return;
    }

    onApply(add, remove);
  };

  const enableAllPatch = buildEnableAllPatch(group, selection, canGrantProtected);

  const enableAll = () => {
    if (addBlocked || !enableAllPatch.length) return;

    runOrConfirm('Enable all', enableAllPatch, []);
  };

  const clearAll = () => {
    const remove = buildClearAllPatch(group, selection);
    if (!remove.length) return;

    runOrConfirm('Clear all', [], remove);
  };

  const touchedCount = (pending?.add.length ?? 0) + (pending?.remove.length ?? 0);

  const enableButton = () => {
    const button = (
      <Button
        size="sm"
        variant="outline"
        disabled={addBlocked || enableAllPatch.length === 0}
        onClick={enableAll}
      >
        {`Enable all (${enableAllPatch.length})`}
      </Button>
    );
    const tooltip = addBlocked ? BULK_ADD_BLOCKED_TOOLTIP : null;

    return tooltip ? (
      // A disabled button emits no pointer events, so the tooltip hangs off a wrapper.
      <Tooltip title={tooltip}>
        <span data-testid={addBlocked ? 'bulk-add-blocked' : undefined}>{button}</span>
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <>
      <BulkActionsRow>
        {enableButton()}

        <Button size="sm" variant="ghost" disabled={clearCount === 0} onClick={clearAll}>
          {`Clear all (${clearCount})`}
        </Button>
      </BulkActionsRow>

      <Modal
        open={pending !== null}
        onOpenChange={(isOpen) => !isOpen && setPending(null)}
        variant="center"
        title={pending ? `${pending.label} in ${group.label}?` : ''}
        description={
          pending
            ? pending.sensitiveCount === touchedCount
              ? `This changes ${touchedCount} sensitive permission${touchedCount === 1 ? '' : 's'} in ${group.label}. Review before continuing.`
              : `This changes ${touchedCount} permission${touchedCount === 1 ? '' : 's'} in ${group.label}, including ${pending.sensitiveCount} sensitive one${pending.sensitiveCount === 1 ? '' : 's'}. Review before continuing.`
            : ''
        }
        primaryAction={{
          label: 'Confirm',
          onClick: () => {
            if (pending) onApply(pending.add, pending.remove);
            setPending(null);
          },
        }}
        secondaryAction={{ label: 'Cancel', onClick: () => setPending(null) }}
      >
        <></>
      </Modal>
    </>
  );
};
